// Google Maps static map display
import React, { useState, useEffect } from 'react';
import { useGoogleMaps } from './GoogleMapsConfigProvider';
import { GoogleMapsStaticMapProps } from './types';
import { Loader2, MapPin, AlertCircle } from 'lucide-react';

export const GoogleMapsStaticMap: React.FC<GoogleMapsStaticMapProps> = ({
  latitude,
  longitude,
  zoom = 15,
  width = 400,
  height = 300,
  markerColor = 'red',
  className = '',
  apiKey
}) => {
  const { isLoaded, isLoading, error: apiError } = useGoogleMaps();
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Generate static map URL
  const generateStaticMapUrl = (): string => {
    if (!latitude || !longitude) {
      return '';
    }

    // Convert to numbers to ensure valid coordinates
    const latNum = Number(latitude);
    const lngNum = Number(longitude);
    
    if (isNaN(latNum) || isNaN(lngNum)) {
      return '';
    }

    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const center = `${latNum},${lngNum}`;
    const size = `${width}x${height}`;
    const marker = `color:${markerColor}|label:M|${latNum},${lngNum}`;
    const scale = window.devicePixelRatio > 1 ? '2' : '1';

    const params = new URLSearchParams({
      center,
      zoom: zoom.toString(),
      size,
      markers: marker,
      scale,
      key: apiKey || ''
    });

    // Add map type and language
    params.append('maptype', 'roadmap');
    params.append('language', 'fr');

    return `${baseUrl}?${params.toString()}`;
  };

  const staticMapUrl = generateStaticMapUrl();

  // Handle image load error
  const handleImageError = () => {
    setMapError('Impossible de charger la carte');
    setMapLoaded(false);
  };

  // Handle image load success
  const handleImageLoad = () => {
    setMapLoaded(true);
    setMapError(null);
  };

  // Reset state when coordinates change
  useEffect(() => {
    setMapLoaded(false);
    setMapError(null);
  }, [latitude, longitude, zoom, width, height]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={`relative ${className}`} style={{ width, height }}>
        <div className="flex items-center justify-center w-full h-full border border-gray-300 rounded-md bg-gray-50">
          <Loader2 className="w-8 h-8 mr-2 animate-spin text-gray-500" />
          <span className="text-gray-600">Chargement de Google Maps...</span>
        </div>
      </div>
    );
  }

  // Show API error
  if (apiError) {
    return (
      <div className={`relative ${className}`} style={{ width, height }}>
        <div className="flex flex-col items-center justify-center w-full h-full p-4 border border-red-300 rounded-md bg-red-50">
          <AlertCircle className="w-8 h-8 mb-2 text-red-500" />
          <span className="text-red-600 text-center">Google Maps non disponible</span>
          <span className="text-red-500 text-sm text-center mt-1">{apiError}</span>
        </div>
      </div>
    );
  }

  // Show error for missing coordinates
  if (!latitude || !longitude) {
    return (
      <div className={`relative ${className}`} style={{ width, height }}>
        <div className="flex flex-col items-center justify-center w-full h-full p-4 border border-yellow-300 rounded-md bg-yellow-50">
          <MapPin className="w-8 h-8 mb-2 text-yellow-500" />
          <span className="text-yellow-700 text-center">Aucune localisation disponible</span>
          <span className="text-yellow-600 text-sm text-center mt-1">
            Les coordonnées de la carte ne sont pas définies
          </span>
        </div>
      </div>
    );
  }

  // Show error for missing API key
  if (!apiKey && !isLoaded) {
    return (
      <div className={`relative ${className}`} style={{ width, height }}>
        <div className="flex flex-col items-center justify-center w-full h-full p-4 border border-orange-300 rounded-md bg-orange-50">
          <AlertCircle className="w-8 h-8 mb-2 text-orange-500" />
          <span className="text-orange-700 text-center">Clé API Google Maps requise</span>
          <span className="text-orange-600 text-sm text-center mt-1">
            Configurez une clé API pour afficher les cartes
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {/* Static map image */}
      {staticMapUrl && (
        <div className="relative w-full h-full">
          <img
            src={staticMapUrl}
            alt={`Carte de localisation à ${latitude}, ${longitude}`}
            className={`w-full h-full object-cover border border-gray-300 rounded-md transition-opacity duration-300 ${
              mapLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />

          {/* Loading overlay */}
          {!mapLoaded && !mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 border border-gray-300 rounded-md">
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          )}

          {/* Error overlay */}
          {mapError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 border border-red-300 rounded-md p-4">
              <AlertCircle className="w-6 h-6 mb-2 text-red-500" />
              <span className="text-red-600 text-sm text-center">{mapError}</span>
            </div>
          )}

          {/* Coordinates overlay */}
          {mapLoaded && !mapError && (
            <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs p-2 rounded">
              <div className="flex justify-between">
                <span>Latitude: {Number(latitude).toFixed(6)}</span>
                <span>Longitude: {Number(longitude).toFixed(6)}</span>
              </div>
              <div className="text-center mt-1">
                Zoom: {zoom} • {width}×{height}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map attribution */}
      {mapLoaded && !mapError && (
        <div className="mt-1 text-xs text-gray-500 text-center">
          <a
            href="https://maps.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Google Maps
          </a>
          {' • '}
          <a
            href={`https://www.google.com/maps/@${latitude},${longitude},${zoom}z`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Ouvrir dans Google Maps
          </a>
        </div>
      )}
    </div>
  );
};